import { Chip, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import {
	BooleanInput,
	Datagrid,
	ExportButton,
	FilterButton,
	FunctionField,
	List,
	NumberInput,
	SelectArrayInput,
	SelectInput,
	TextInput,
	TopToolbar,
	useGetList,
} from 'react-admin';
import { downloadEncodedCSV } from '../../utils/functions';
import MyPagination from '../MyPagination';

// GH-749 (Yael, tech support) — "מאגר מומחים לאירועים": choose the best experts for an external event
// (conferences etc.). Separate from the experts list on purpose: that list is the ops tool (onboarding
// status, inactive experts, calendar checks) and stays as is. Numbers come from admin/expert-ranking:
// member ratings of the last 12 months, lecture ratings, lectures held, private sessions in 90 days.

const KIND_LABEL: Record<string, string> = { expert: 'מומחה/ית', therapist: 'מטפל/ת' };
// 'unknown' = the expert's gender isn't really filled in (empty / 'other')
const GENDER_LABEL: Record<string, string> = { female: 'אישה', male: 'גבר', unknown: 'לא ידוע' };

const stars = (value: number | null, count: number) =>
	value == null ? (
		<Typography variant="body2" color="text.disabled">
			—
		</Typography>
	) : (
		<Tooltip title={`${count} דירוגים`}>
			<Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
				⭐ {value.toFixed(2)} <span style={{ color: '#888' }}>({count})</span>
			</Typography>
		</Tooltip>
	);

const exporter = (rows: any[]) =>
	downloadEncodedCSV(
		rows.map((r) => ({
			שם: r.name,
			סוג: KIND_LABEL[r.kind] || '',
			מגדר: GENDER_LABEL[r.gender] || '',
			התמחויות: (r.categories || []).map((c: any) => c.name).join(', '),
			'דירוג ממוצע': r.rating_avg ?? '',
			'מספר דירוגים': r.rating_count,
			'דירוג הרצאות': r.lecture_rating_avg ?? '',
			'דירוגי הרצאות': r.lecture_rating_count,
			'הרצאות ב-12 חודשים': r.lectures_12m,
			'ממוצע נרשמים להרצאה': r.lecture_registrants_avg ?? '',
			'פגישות ב-90 יום': r.sessions_90d,
			תיאור: r.title || '',
		})),
		undefined,
		`מאגר_מומחים_${moment().format('YYYY_MM_DD')}`,
	);

const ExpertRankingList = () => {
	const { data: categories = [] } = useGetList('expert-ranking-categories', { pagination: { page: 1, perPage: 500 } });

	const filters = [
		<TextInput key="q" source="q" label="חיפוש לפי שם / תיאור" alwaysOn />,
		<SelectArrayInput
			key="categories"
			source="categories"
			label="התמחויות"
			choices={categories.map((c: any) => ({ id: c.id, name: c.name }))}
			alwaysOn
			sx={{ minWidth: 220 }}
		/>,
		<SelectInput
			key="kind"
			source="kind"
			label="סוג"
			choices={[
				{ id: 'expert', name: 'מומחים' },
				{ id: 'therapist', name: 'מטפלים' },
			]}
			alwaysOn
		/>,
		<SelectInput
			key="gender"
			source="gender"
			label="מגדר"
			choices={[
				{ id: 'female', name: 'אישה' },
				{ id: 'male', name: 'גבר' },
				{ id: 'unknown', name: 'לא ידוע' },
			]}
			alwaysOn
		/>,
		<NumberInput key="minRatings" source="minRatings" label="לפחות X דירוגים" min={0} alwaysOn sx={{ width: 150 }} />,
		<BooleanInput key="showAll" source="showAll" label="הצג את כולם (גם לא פעילים / לא במנוי / גלובליים)" />,
	];

	return (
		<List
			resource="expert-ranking"
			title="מאגר מומחים לאירועים"
			filters={filters}
			sort={{ field: 'rating_avg', order: 'DESC' }}
			perPage={50}
			pagination={<MyPagination />}
			exporter={exporter}
			actions={
				<TopToolbar>
					<FilterButton />
					<ExportButton label="ייצוא לאקסל" maxResults={1000} />
				</TopToolbar>
			}
		>
			<Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
				ברירת מחדל: מומחים פעילים שמופיעים במנוי (לא גלובליים). הדירוגים הם של עובדים ב-12 החודשים האחרונים. לחיצה על שורה פותחת את
				המומחה.
			</Typography>
			<Datagrid bulkActionButtons={false} rowClick={(id) => `/instructor/${id}`}>
				<FunctionField
					label="שם"
					sortBy="name"
					render={(r: any) => (
						<div>
							<Typography variant="body2" fontWeight={600}>
								{r.name}
							</Typography>
							{r.title && (
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 320 }}>
									{r.title}
								</Typography>
							)}
						</div>
					)}
				/>
				<FunctionField label="סוג" sortable={false} render={(r: any) => KIND_LABEL[r.kind] || ''} />
				<FunctionField label="מגדר" sortable={false} render={(r: any) => GENDER_LABEL[r.gender] || ''} />
				<FunctionField
					label="התמחויות"
					sortable={false}
					render={(r: any) => (
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 280 }}>
							{(r.categories || []).slice(0, 4).map((c: any) => (
								<Chip key={c.id} label={c.name} size="small" />
							))}
							{(r.categories || []).length > 4 && <Chip label={`+${r.categories.length - 4}`} size="small" variant="outlined" />}
						</div>
					)}
				/>
				<FunctionField label="⭐ דירוג" sortBy="rating_avg"
					sortByOrder="DESC" render={(r: any) => stars(r.rating_avg, r.rating_count)} />
				<FunctionField label="מס' דירוגים" sortBy="rating_count"
					sortByOrder="DESC" render={(r: any) => r.rating_count} />
				<FunctionField
					label="⭐ דירוג הרצאות"
					sortBy="lecture_rating_avg"
					sortByOrder="DESC"
					render={(r: any) => stars(r.lecture_rating_avg, r.lecture_rating_count)}
				/>
				<FunctionField label="הרצאות (12 חודשים)" sortBy="lectures_12m"
					sortByOrder="DESC" render={(r: any) => r.lectures_12m} />
				<FunctionField
					label={
						<Tooltip title="כמה נרשמים (עובדים + אורחים) יש בממוצע להרצאה של המומחה, בהרצאות שהתקיימו ב-12 החודשים האחרונים">
							<span>פופולריות הרצאות (ממוצע נרשמים)</span>
						</Tooltip>
					}
					sortBy="lecture_registrants_avg"
					sortByOrder="DESC"
					render={(r: any) => (r.lecture_registrants_avg == null ? '—' : r.lecture_registrants_avg)}
				/>
				<FunctionField
					label={
						<Tooltip title="פגישות אישיות ששולמו ולא בוטלו ב-90 הימים האחרונים — כמה עובדים בוחרים במומחה">
							<span>פופולריות פגישות (90 יום)</span>
						</Tooltip>
					}
					sortBy="sessions_90d"
					sortByOrder="DESC"
					render={(r: any) => r.sessions_90d}
				/>
				<FunctionField
					label="סלוטים פנויים בשבוע"
					sortBy="availableSlotsNextWeek"
					sortByOrder="DESC"
					render={(r: any) => (r.availableSlotsNextWeek == null || r.availableSlotsNextWeek < 0 ? '—' : r.availableSlotsNextWeek)}
				/>
			</Datagrid>
		</List>
	);
};

export default ExpertRankingList;
